import React, { useMemo, useState } from 'react';
import Collapse from './Collapse';
import styled from '@emotion/styled';
import { Colors, device } from 'utils';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

export default function ProjectFAQs({ projectFaq = [], isTicketable, isPatreon }) {
  const [currentOpen, setCurrentOpen] = useState(null);

  const handleCurrentOpen = (isOpen, questionKey) => {
    setCurrentOpen(isOpen ? questionKey : null);
  };

  const faqItems = useMemo(() => {
    return getFaqItems({ isTicketable, isPatreon });
  }, [isTicketable, isPatreon]);

  return (
    <ProjectFAQsWrapper>
      <FAQTitle>
        {isTicketable
          ? 'Eventinformation im Überblick'
          : 'Projektinformationen im Überblick'}
      </FAQTitle>

      <div>
        {faqItems.map(({ key, title, faqIndex, arrowWidth }) => (
          <FAQCollapse
            key={key}
            itemKey={key}
            title={title}
            value={projectFaq?.[faqIndex]}
            isOpen={currentOpen === key}
            arrowWidth={arrowWidth}
            onToggle={handleCurrentOpen}
          />
        ))}
      </div>
    </ProjectFAQsWrapper>
  );
}

function FAQCollapse({
  itemKey,
  title,
  value,
  isOpen,
  arrowWidth,
  onToggle,
}) {
  return (
    <Collapse
      titleMessage={<div className="text-ab">{title}</div>}
      arrowWidth={arrowWidth}
      className="collapse-data"
      defaultState={isOpen}
      handleMultiCollapes={onToggle}
      witchQuestions={itemKey}
    >
      <CollapseText
        className="text_area"
        dangerouslySetInnerHTML={{
          __html:
            DOMPurify.sanitize(value?.replace(/\\r\\n|\\n|\\r/g, '\n') || '', {
              ALLOWED_TAGS: [
                'b',
                'strong',
                'i',
                'em',
                'u',
                'p',
                'br',
                'ul',
                'ol',
                'li',
                'a',
              ],
              ALLOWED_ATTR: ['href', 'target', 'rel'],
            }) || '',
        }}
      />
    </Collapse>
  );
}

function getFaqItems({ isTicketable, isPatreon }) {
  const projectInfoTitle = getProjectInfoTitle({ isTicketable, isPatreon });

  const items = [
    {
      key: 'projectInfo',
      faqIndex: 0,
      title: projectInfoTitle,
      arrowWidth: isPatreon ? '20px' : undefined,
    },
    {
      key: 'projectGoal',
      faqIndex: 1,
      title: isPatreon
        ? 'Wieso solltest du mich unterstützen?'
        : 'Was sind die Ziele – und wer ist die Zielgruppe?',
    },
  ];

  if (!isTicketable) {
    items.push(
      {
        key: 'projectSupport',
        faqIndex: 2,
        title: isPatreon
          ? 'Wofür wird dein Support verwendet?'
          : 'Wofür wird das Geld benötigt?',
      },
      {
        key: 'projectMoney',
        faqIndex: 3,
        title: isPatreon
          ? 'Was erhältst du für deinen Support?'
          : ' Wie wird mit den Unterstützungen umgegangen?',
      }
    );
  }

  if (isTicketable) {
    items.push({
      key: 'projectPeople',
      faqIndex: 4,
      title: 'Wer sind die Organisatoren der Veranstaltung?',
    });
  }

  if (!isTicketable && !isPatreon) {
    items.push({
      key: 'projectPeople',
      faqIndex: 4,
      title: 'Wer ist in diesem Projekt involviert?',
    });
  }

  return items;
}

function getProjectInfoTitle({ isTicketable, isPatreon }) {
  if (isTicketable) {
    return 'Was ist der Zweck der Veranstaltung?';
  }

  if (isPatreon) {
    return 'Wen unterstützt du?';
  }

  return 'Was ist der genaue Zweck dieses Projektes?';
}

ProjectFAQs.propTypes = {
  projectFaq: PropTypes.array,
  isTicketable: PropTypes.bool,
  isPatreon: PropTypes.bool,
};

FAQCollapse.propTypes = {
  itemKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  arrowWidth: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
};

const ProjectFAQsWrapper = styled.div`
  padding: 5px 10px;

  .text-ab {
    /* Text */
    font-family: 'Inter';
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    /* or 156% */

    color: #181d27;

    @media ${device.allMobile} {
      /* Text md/Medium */
      font-family: 'Inter';
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      /* or 150% */
      color: #181d27;
    }
  }
  .collapse-data {
    padding-bottom: 24px;
    margin-bottom: 24px;
    border-bottom: 1px solid #e9eaeb;

    textarea {
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
      color: #535862;

      @media ${device.allMobile} {
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
        color: #535862;
      }
    }

    h2 {
      font-size: 22px;
      font-weight: 600;
    }

    ul li {
      font-size: 14px;
      font-weight: 400;
      color: ${Colors.steel};
    }

    ol li {
      font-size: 14px;
      font-weight: 400;
      color: ${Colors.steel};
    }
    p {
      font-size: 14px;
      font-weight: 400;
      margin: 0 0 15px;
      color: ${Colors.steel};
    }
  }
`;

const CollapseText = styled.div`
  overflow-wrap: anywhere;

  word-break: break-word;

  white-space: pre-wrap;
  margin-top: 8px;
  /* Text md/Regular */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* or 150% */

  color: #535862;
`;

const FAQTitle = styled.h2`
  
font-family: 'Inter';
font-style: normal;
font-weight: 600;
font-size: 24px;
line-height: 44px;
/* identical to box height, or 183% */
letter-spacing: -0.02em;

color: #181D27;
`